/**
 * FLOOR CORE DEFINITION (2026-08-18, Matt — the WALL CORE DEFINITION pattern applied
 * to floors, same round): a floor TYPE declares WHAT its structural core is (Wood
 * Joists / TJI Joists / Concrete) and, for the joist families, WHICH curated size —
 * the core layer's material and thickness DERIVE from that declaration. Concrete
 * floors keep a manually-entered slab thickness AND a free material choice (unlike a
 * wall's forced CMU/Concrete material — 'Concrete Slab' vs 'Concrete Paving' are both
 * legitimate slab cores, so the concrete branch locks nothing).
 *
 * SHEATHING IS SEPARATE FROM JOISTS now: the joist material is just 'Wood Joist'
 * (no '+ Sheathing') and the subfloor lives as its own substrate layer above the
 * core — which the floor framing generator already understands
 * (sheathingOutsideCoreFt > 0 → the derived deck IS that layer and joists fill the
 * whole core; the legacy ¾″ carve-from-core only ever applied to the old bundled
 * lump). floorCoreMigration.ts is what splits existing assemblies.
 *
 * Pure data + pure functions (the wallCore.ts convention) — schema choice lists,
 * conform-on-write, naming, and the type editors all read from HERE.
 */
import type { AssemblyLayer, MaterialId, ParamValue } from './types'
import type { WallCoreSize } from './wallCore'
import { nearestCoreSize } from './wallCore'

export const FLOOR_CORE_TYPES = [
  { value: 'wood', label: 'Wood Joists' },
  { value: 'tji', label: 'TJI Joists' },
  { value: 'concrete', label: 'Concrete' },
  // PLANTER SOIL (2026-10-05, Matt: "a slab type … that is basically soil for planter
  // regions that are not part of the topo terrain, like in raised planters"): a soil
  // fill drawn like a slab — manual depth (the concrete rule), its material locked to
  // the canonical 'Planting Soil', never structure (not in the Foundations system)
  { value: 'soil', label: 'Planting Soil' },
  // SITE SURFACES (2026-10-07, Matt: "add water and grass as two new site surfaces"):
  // the soil fill's siblings — a lawn and a body of water (pond, reflecting pool,
  // water feature), each drawn by outline like a slab, never structure
  { value: 'grass', label: 'Grass' },
  { value: 'water', label: 'Water' },
] as const
export type FloorCoreType = (typeof FLOOR_CORE_TYPES)[number]['value']

/** sawn-lumber joist family — nominal names, ACTUAL depths */
export const WOOD_JOIST_SIZES: WallCoreSize[] = [
  { value: '2x6', label: '2×6', actualIn: 5.5, actualLabel: '5½″' },
  { value: '2x8', label: '2×8', actualIn: 7.25, actualLabel: '7¼″' },
  { value: '2x10', label: '2×10', actualIn: 9.25, actualLabel: '9¼″' },
  { value: '2x12', label: '2×12', actualIn: 11.25, actualLabel: '11¼″' },
]

/** engineered I-joist family — named by actual depth (the industry convention) */
export const TJI_JOIST_SIZES: WallCoreSize[] = [
  { value: '9-1/2', label: '9½″', actualIn: 9.5, actualLabel: '9½″' },
  { value: '11-7/8', label: '11⅞″', actualIn: 11.875, actualLabel: '11⅞″' },
  { value: '14', label: '14″', actualIn: 14, actualLabel: '14″' },
  { value: '16', label: '16″', actualIn: 16, actualLabel: '16″' },
]

/** plan width of a derived joist, per family: sawn = 2× stock (1½″, the stud
 *  convention), TJI = the flange width (2½″ — representation-grade, the common
 *  210/230-series flange; the I profile itself is a recorded deferral) */
export const TJI_WIDTH_IN = 2.5

/** The canonical joist materials — concrete deliberately absent (free choice). */
export const FLOOR_CORE_MATERIALS: Partial<Record<FloorCoreType, {
  name: string
  color: string
  category: string
}>> = {
  wood: { name: 'Wood Joist', color: '#c9a86a', category: 'Wood' },
  tji: { name: 'TJI Joist', color: '#d6b36f', category: 'Wood' },
  soil: { name: 'Planting Soil', color: '#5a4632', category: 'Site' },
  grass: { name: 'Grass', color: '#7fa05c', category: 'Site' },
  water: { name: 'Water', color: '#3f86a8', category: 'Site' },
}

/** SITE SURFACES: the floor cores that are site work, never structure */
export const SITE_SURFACE_CORES = ['soil', 'grass', 'water'] as const
export type SiteSurfaceCore = (typeof SITE_SURFACE_CORES)[number]

/** SITE SURFACES: which site surface this floor TYPE is (by its declaration), else null */
export function siteSurfaceOfParams(params: Record<string, unknown> | null | undefined): SiteSurfaceCore | null {
  const v = String(params?.coreType ?? '')
  return (SITE_SURFACE_CORES as readonly string[]).includes(v) ? (v as SiteSurfaceCore) : null
}

/** PLANTER SOIL: is this floor TYPE's core planting soil (by its declaration) */
export function isSoilFloorParams(params: Record<string, unknown> | null | undefined): boolean {
  return String(params?.coreType ?? '') === 'soil'
}

const coreTypeOf = (params: Record<string, ParamValue>): FloorCoreType => {
  const v = String(params.coreType ?? 'wood')
  return (FLOOR_CORE_TYPES.some((t) => t.value === v) ? v : 'wood') as FloorCoreType
}

const sizeOf = (family: WallCoreSize[], value: ParamValue, fallback: string): WallCoreSize =>
  family.find((s) => s.value === String(value)) ?? family.find((s) => s.value === fallback)!

/** the resolved joist-size entry, or null for concrete (no curated size family) */
export function floorCoreSize(params: Record<string, ParamValue>): WallCoreSize | null {
  const ct = coreTypeOf(params)
  if (ct === 'wood') return sizeOf(WOOD_JOIST_SIZES, params.woodJoistSize, '2x10')
  if (ct === 'tji') return sizeOf(TJI_JOIST_SIZES, params.tjiJoistSize, '11-7/8')
  return null
}

/** DERIVED core thickness in feet — null for concrete (slab depth is manual).
 *  4-decimal quantum, the wallCore convention (clipper SCALE + seed values). */
export function floorCoreThicknessFt(params: Record<string, ParamValue>): number | null {
  const size = floorCoreSize(params)
  return size ? Math.round((size.actualIn / 12) * 1e4) / 1e4 : null
}

/** the core segment of the auto type name: '2×10 Wood Joist' / '11⅞″ TJI' — null for
 *  concrete (the name falls back to the actual core material, Slab vs Paving) */
export function floorCoreLabel(params: Record<string, ParamValue>): string | null {
  const ct = coreTypeOf(params)
  const size = floorCoreSize(params)
  if (ct === 'wood') return `${size!.label} Wood Joist`
  if (ct === 'tji') return `${size!.label} TJI`
  return null
}

/** the derived joist plan width for the framing generator, per family */
export function floorJoistWidthIn(params: Record<string, ParamValue>): number {
  return coreTypeOf(params) === 'tji' ? TJI_WIDTH_IN : 1.5
}

/** Conform the first core layer to the declaration (wallCore's contract): material +
 *  derived thickness for the joist families; a no-op (null) for concrete. */
export function conformFloorCoreLayers(
  params: Record<string, ParamValue>,
  layers: AssemblyLayer[],
  core: { start: number; end: number },
  materialIdByName: (name: string) => string | null,
): AssemblyLayer[] | null {
  if (!layers.length) return null
  const spec = FLOOR_CORE_MATERIALS[coreTypeOf(params)]
  if (!spec) return null                              // concrete: nothing derived
  const coreIdx = Math.min(Math.max(core.start, 0), layers.length - 1)
  const matId = materialIdByName(spec.name)
  const thicknessFt = floorCoreThicknessFt(params)
  const cur = layers[coreIdx]
  const nextMat = (matId ?? cur.materialId) as MaterialId
  const nextT = thicknessFt ?? cur.thicknessFt
  if (cur.materialId === nextMat && Math.abs(cur.thicknessFt - nextT) < 1e-9) return null
  return layers.map((l, i) => (i === coreIdx ? { ...l, materialId: nextMat, thicknessFt: nextT } : l))
}

/** Classify a legacy floor core material into a family — the migration's parse. */
export function classifyFloorCoreMaterial(name: string, category?: string): FloorCoreType {
  if (/\btji\b/i.test(name)) return 'tji'
  if (/\bsoil\b/i.test(name)) return 'soil'
  if (/\b(grass|lawn|turf|sod)\b/i.test(name)) return 'grass'
  if (/^water\b/i.test(name)) return 'water'
  if (category === 'Wood' || /\b(wood|joist|timber|lumber)\b/i.test(name)) return 'wood'
  return 'concrete'
}

export { nearestCoreSize }
